"use client"

import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { Cross2Icon } from "@radix-ui/react-icons"
import { cn } from "@/lib/utils"
import { convertToLocale } from "@lib/util/money"
import { HttpTypes } from "@medusajs/types"
import { Button } from "@/components/ui/button"
import DeleteButton from "@modules/common/components/delete-button"
import LineItemPrice from "@modules/common/components/line-item-price"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import Thumbnail from "@modules/products/components/thumbnail"
import { useTranslations, useLocale } from "next-intl"
import { ShoppingCart } from "lucide-react"
import { getLocalizedItemTitle } from "@lib/util/cart-localization"

interface CartDrawerProps {
  cart?: HttpTypes.StoreCart | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

const CartDrawer = ({ cart, open, onOpenChange }: CartDrawerProps) => {
  const t = useTranslations("cart")
  const tDropdown = useTranslations("cart.dropdown")
  const tCommon = useTranslations("common")
  const locale = useLocale()
  const intlLocaleMap: Record<string, string> = {
    ru: "ru-RU", en: "en-US", cs: "cs-CZ", uk: "uk-UA",
  }

  // Hide service line items (return shipping, etc.) — they belong to the
  // checkout summary, not the user-facing list of products in the cart.
  // Match the cart-page filter so both views stay in sync.
  const visibleItems = (cart?.items || []).filter((item) => {
    const meta = (item.metadata as any) || {}
    return !meta.isReturnShipping && !meta.isServiceItem
  })

  const totalItems = visibleItems.reduce((acc, item) => acc + item.quantity, 0)
  // Items-only total (no return-shipping line). cart.item_total still
  // includes service line items because Medusa doesn't filter them out;
  // sum the visible ones ourselves.
  const itemTotal = visibleItems.reduce((acc, item) => acc + (item.total ?? 0), 0)

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className="fixed inset-0 z-50 bg-black/50 radix-state-open:animate-in radix-state-closed:animate-out radix-state-closed:fade-out-0 radix-state-open:fade-in-0"
        />
        <DialogPrimitive.Content
          className={cn(
            "fixed right-0 top-0 z-50 h-full w-full max-w-md bg-background shadow-xl",
            "flex flex-col",
            "duration-300 ease-in-out",
            "radix-state-open:animate-in radix-state-closed:animate-out",
            "radix-state-open:slide-in-from-right radix-state-closed:slide-out-to-right"
          )}
          aria-label={t("title")}
          aria-busy={false}
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-border px-6 py-4">
            <DialogPrimitive.Title className="text-lg font-semibold">
              {t("title")} ({totalItems})
            </DialogPrimitive.Title>
            <DialogPrimitive.Close
              className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2"
              aria-label={tCommon("buttons.close")}
            >
              <Cross2Icon className="h-5 w-5" />
            </DialogPrimitive.Close>
          </div>

          {/* Cart Items */}
          {cart && visibleItems.length ? (
            <>
              <div className="flex-1 overflow-y-auto px-6 py-4">
                <div className="flex flex-col gap-y-6">
                  {visibleItems
                    .sort((a, b) =>
                      (a.created_at ?? "") > (b.created_at ?? "") ? -1 : 1
                    )
                    .map((item) => (
                      <div
                        className="grid grid-cols-[80px_1fr] gap-x-4"
                        key={item.id}
                        data-testid="cart-drawer-item"
                      >
                        <LocalizedClientLink
                          href={`/products/${item.product_handle}`}
                          onClick={() => onOpenChange(false)}
                        >
                          <Thumbnail
                            thumbnail={
                              item.thumbnail ||
                              item.variant?.product?.thumbnail
                            }
                            images={item.variant?.product?.images}
                            size="square"
                          />
                        </LocalizedClientLink>
                        <div className="flex flex-col justify-between flex-1 min-w-0">
                          <div>
                            <h3 className="text-sm font-medium truncate">
                              <LocalizedClientLink
                                href={`/products/${item.product_handle}`}
                                onClick={() => onOpenChange(false)}
                              >
                                {getLocalizedItemTitle(item, locale)}
                              </LocalizedClientLink>
                            </h3>
                            <span className="text-xs text-muted-foreground">
                              {tDropdown("quantity", {
                                quantity: item.quantity,
                              })}
                            </span>
                          </div>
                          <div className="flex items-center justify-between mt-1">
                            <DeleteButton
                              id={item.id}
                              data-testid="cart-drawer-remove-button"
                            >
                              {t("remove")}
                            </DeleteButton>
                            <LineItemPrice
                              item={item}
                              style="tight"
                              currencyCode={cart.currency_code}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>

              {/* Footer with totals and actions */}
              <div className="border-t border-border px-6 py-4 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">
                    {tDropdown("subtotal")}
                  </span>
                  <span className="text-base font-semibold">
                    {convertToLocale({
                      amount: itemTotal,
                      currency_code: cart.currency_code,
                    })}
                  </span>
                </div>

                <Button
                  asChild
                  className="w-full"
                  size="large"
                  data-testid="cart-drawer-checkout-button"
                >
                  <LocalizedClientLink
                    href="/checkout"
                    onClick={() => onOpenChange(false)}
                  >
                    {t("goToCheckout")}
                  </LocalizedClientLink>
                </Button>

                <Button
                  variant="ghost"
                  className="h-auto p-0 rounded-none font-normal w-full text-center text-sm text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => onOpenChange(false)}
                >
                  {t("continueShopping")}
                </Button>
              </div>
            </>
          ) : (
            /* Empty state */
            <div className="flex-1 flex flex-col items-center justify-center gap-y-4 px-6">
              <ShoppingCart className="h-12 w-12 text-muted-foreground" />
              <p className="text-muted-foreground">{tDropdown("emptyBag")}</p>
              <LocalizedClientLink
                href="/store"
                onClick={() => onOpenChange(false)}
              >
                <Button>{tDropdown("explore")}</Button>
              </LocalizedClientLink>
            </div>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

export default CartDrawer
